import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAdminResources, getAdminCategories, createResource, updateResource, deleteResource, createCategory, deleteCategory, uploadResourceFile } from '../api';

const POSITIONS = [
  'all',
  'Occupational Therapist',
  'Speech-Language Pathologist',
  'School Psychologist',
  'Special Education Teacher',
  'Administrator',
  'Other',
];

export default function AdminResources() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editResource, setEditResource] = useState(null);
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [form, setForm] = useState({ title: '', description: '', file_url: '', file_name: '', category_ids: [], positions: ['all'] });
  const [uploading, setUploading] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkCategoryIds, setBulkCategoryIds] = useState([]);
  const [bulkPositions, setBulkPositions] = useState(['all']);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const [resRes, catRes] = await Promise.all([getAdminResources(), getAdminCategories()]);
    setResources(resRes.data);
    setCategories(catRes.data);
    setLoading(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadResourceFile(formData);
      setForm(prev => ({ ...prev, file_url: res.data.file_url, file_name: res.data.file_name }));
      setMsg('File uploaded successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not upload file.');
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFiles.length) return;
    setBulkUploading(true);
    setError('');
    const results = [];
    for (const file of bulkFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await uploadResourceFile(formData);
        await createResource({
          title: file.name.replace('.pdf', '').replace(/-/g, ' ').replace(/_/g, ' '),
          file_url: uploadRes.data.file_url,
          file_name: uploadRes.data.file_name,
          category_ids: bulkCategoryIds,
          positions: bulkPositions
        });
        results.push({ name: file.name, success: true });
      } catch (err) {
        results.push({ name: file.name, success: false, error: err.response?.data?.error || 'Failed' });
      }
    }
    setBulkResults(results);
    setBulkUploading(false);
    setMsg(`Uploaded ${results.filter(r => r.success).length} of ${results.length} files`);
    fetchData();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await createResource({
        ...form,
        category_id: form.category_ids[0] || null,
        category_ids: form.category_ids
      });
      setMsg('Resource created successfully!');
      setShowAdd(false);
      setForm({ title: '', description: '', file_url: '', file_name: '', category_ids: [], positions: ['all'] });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create resource.');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await updateResource(editResource.id, {
        ...editResource,
        category_id: editResource.category_ids?.[0] || null,
        category_ids: editResource.category_ids || [],
        positions: editResource.positions || ['all']
      });
      setMsg('Resource updated successfully!');
      setEditResource(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update resource.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    await deleteResource(id);
    setMsg('Resource deleted.');
    fetchData();
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await createCategory({ name: newCatName });
      setMsg('Category added!');
      setShowAddCat(false);
      setNewCatName('');
      fetchData();
    } catch (err) {
      setError('Could not add category.');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await deleteCategory(id);
    setMsg('Category deleted.');
    fetchData();
  };

  const toggleItem = (item, list, setter) => {
    if (list.includes(item)) setter(list.filter(i => i !== item));
    else setter([...list, item]);
  };

  const toggleCategoryId = (id, currentIds, setter) => {
    if (currentIds.includes(id)) setter(currentIds.filter(i => i !== id));
    else setter([...currentIds, id]);
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div style={styles.container}>
      <nav style={styles.nav}>
        <div style={styles.navBrand}>
          <span style={styles.navTitle}>Boothby Therapy Services</span>
          <span style={styles.navSubtitle}>Admin Panel</span>
        </div>
        <div style={styles.navRight}>
          <button onClick={() => navigate('/admin')} style={styles.navBtn}>🏠 Dashboard</button>
          <button onClick={() => navigate('/admin/users')} style={styles.navBtn}>👥 Users</button>
          <button onClick={() => navigate('/admin/profile')} style={styles.navBtn}>⚙️ Settings</button>
          <button onClick={handleLogout} style={styles.navBtnOutline}>Sign Out</button>
        </div>
      </nav>

      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.pageTitle}>Manage Resources</h1>
          <div style={styles.headerBtns}>
            <button onClick={() => setShowAddCat(true)} style={styles.secondaryBtn}>+ Category</button>
            <button onClick={() => setShowBulkUpload(true)} style={styles.secondaryBtn}>📋 Bulk Upload</button>
            <button onClick={() => setShowAdd(true)} style={styles.addBtn}>+ Add Resource</button>
          </div>
        </div>

        {msg && <div style={styles.success}>{msg}</div>}
        {error && <div style={styles.error}>{error}</div>}

        {/* Categories */}
        <div style={styles.catSection}>
          <h3 style={styles.sectionTitle}>Categories</h3>
          <div style={styles.catList}>
            {categories.map(cat => (
              <div key={cat.id} style={styles.catTag}>
                {cat.name}
                <button onClick={() => handleDeleteCategory(cat.id)} style={styles.catDeleteBtn}>×</button>
              </div>
            ))}
          </div>
        </div>

        {/* Add Category Modal */}
        {showAddCat && (
          <div style={styles.modal}>
            <div style={styles.modalCard}>
              <h2 style={styles.modalTitle}>Add Category</h2>
              <form onSubmit={handleAddCategory} style={styles.form}>
                <div style={styles.field}>
                  <label style={styles.label}>Category Name</label>
                  <input style={styles.input} value={newCatName} onChange={(e) => setNewCatName(e.target.value)} required />
                </div>
                <div style={styles.modalBtns}>
                  <button type="submit" style={styles.addBtn}>Add Category</button>
                  <button type="button" onClick={() => setShowAddCat(false)} style={styles.cancelBtn}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showBulkUpload && (
          <div style={styles.modal}>
            <div style={styles.modalCard}>
              <h2 style={styles.modalTitle}>📋 Bulk Upload PDFs</h2>
              <div style={styles.infoBox}>
                <p style={styles.infoText}>📌 Each PDF's title will be auto-generated from its file name. You can edit titles after uploading.</p>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Select Multiple PDF Files</label>
                <input
                  type="file"
                  accept="application/pdf"
                  multiple
                  style={styles.input}
                  onChange={(e) => setBulkFiles(Array.from(e.target.files))}
                />
                {bulkFiles.length > 0 && (
                  <span style={styles.uploadedText}>✅ {bulkFiles.length} file(s) selected</span>
                )}
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Categories (for all files)</label>
                <div style={styles.checkboxList}>
                  {categories.map(cat => (
                    <label key={cat.id} style={styles.positionItem}>
                      <input
                        type="checkbox"
                        checked={bulkCategoryIds.includes(cat.id)}
                        onChange={() => toggleCategoryId(cat.id, bulkCategoryIds, setBulkCategoryIds)}
                      />
                      {cat.name}
                    </label>
                  ))}
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Access by Position (for all files)</label>
                <div style={styles.positionList}>
                  {POSITIONS.map(pos => (
                    <label key={pos} style={styles.positionItem}>
                      <input
                        type="checkbox"
                        checked={bulkPositions.includes(pos)}
                        onChange={() => toggleItem(pos, bulkPositions, setBulkPositions)}
                      />
                      {pos === 'all' ? 'All Employees' : pos}
                    </label>
                  ))}
                </div>
              </div>
              {bulkResults.length > 0 && (
                <div style={styles.bulkResults}>
                  {bulkResults.map((r, i) => (
                    <div key={i} style={{fontSize: '13px', color: r.success ? '#16a34a' : '#dc2626'}}>
                      {r.success ? '✅' : '❌'} {r.name} {!r.success && `- ${r.error}`}
                    </div>
                  ))}
                </div>
              )}
              <div style={styles.modalBtns}>
                <button onClick={handleBulkUpload} style={styles.addBtn} disabled={bulkUploading || !bulkFiles.length}>
                  {bulkUploading ? 'Uploading...' : `Upload ${bulkFiles.length} File(s)`}
                </button>
                <button type="button" onClick={() => { setShowBulkUpload(false); setBulkResults([]); setBulkFiles([]); setBulkCategoryIds([]); setBulkPositions(['all']); }} style={styles.cancelBtn}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Add Resource Modal */}
        {showAdd && (
          <div style={styles.modal}>
            <div style={styles.modalCard}>
              <h2 style={styles.modalTitle}>Add New Resource</h2>
              <form onSubmit={handleCreate} style={styles.form}>
                <div style={styles.field}>
                  <label style={styles.label}>Title</label>
                  <input style={styles.input} value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Description</label>
                  <textarea style={{...styles.input, height: '80px'}} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Upload PDF File</label>
                  <input type="file" accept="application/pdf" style={styles.input} onChange={(e) => handleFileUpload(e.target.files[0])} />
                  {uploading && <span style={styles.uploadingText}>⏳ Uploading...</span>}
                  {form.file_url && !uploading && <span style={styles.uploadedText}>✅ {form.file_name}</span>}
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Or paste File URL</label>
                  <input style={styles.input} value={form.file_url} onChange={(e) => setForm({...form, file_url: e.target.value})} placeholder="https://..." />
                </div>

                {/* Multiple Category Select */}
                <div style={styles.field}>
                  <label style={styles.label}>Categories (select multiple)</label>
                  <div style={styles.checkboxList}>
                    {categories.map(cat => (
                      <label key={cat.id} style={styles.positionItem}>
                        <input
                          type="checkbox"
                          checked={form.category_ids.includes(cat.id)}
                          onChange={() => toggleCategoryId(cat.id, form.category_ids, (newIds) => setForm({...form, category_ids: newIds}))}
                        />
                        {cat.name}
                      </label>
                    ))}
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Access by Position</label>
                  <div style={styles.positionList}>
                    {POSITIONS.map(pos => (
                      <label key={pos} style={styles.positionItem}>
                        <input
                          type="checkbox"
                          checked={form.positions.includes(pos)}
                          onChange={() => toggleItem(pos, form.positions, (newPos) => setForm({...form, positions: newPos}))}
                        />
                        {pos === 'all' ? 'All Employees' : pos}
                      </label>
                    ))}
                  </div>
                </div>
                <div style={styles.modalBtns}>
                  <button type="submit" style={styles.addBtn} disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Add Resource'}
                  </button>
                  <button type="button" onClick={() => setShowAdd(false)} style={styles.cancelBtn}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Resource Modal */}
        {editResource && (
          <div style={styles.modal}>
            <div style={styles.modalCard}>
              <h2 style={styles.modalTitle}>Edit Resource</h2>
              <form onSubmit={handleUpdate} style={styles.form}>
                <div style={styles.field}>
                  <label style={styles.label}>Title</label>
                  <input style={styles.input} value={editResource.title} onChange={(e) => setEditResource({...editResource, title: e.target.value})} required />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Description</label>
                  <textarea style={{...styles.input, height: '80px'}} value={editResource.description || ''} onChange={(e) => setEditResource({...editResource, description: e.target.value})} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>File URL</label>
                  <input style={styles.input} value={editResource.file_url} onChange={(e) => setEditResource({...editResource, file_url: e.target.value})} required />
                </div>

                {/* Multiple Category Select */}
                <div style={styles.field}>
                  <label style={styles.label}>Categories (select multiple)</label>
                  <div style={styles.checkboxList}>
                    {categories.map(cat => (
                      <label key={cat.id} style={styles.positionItem}>
                        <input
                          type="checkbox"
                          checked={(editResource.category_ids || []).includes(cat.id)}
                          onChange={() => toggleCategoryId(cat.id, editResource.category_ids || [], (newIds) => setEditResource({...editResource, category_ids: newIds}))}
                        />
                        {cat.name}
                      </label>
                    ))}
                  </div>
                </div>

                <div style={styles.field}>
                  <label style={styles.label}>Access by Position</label>
                  <div style={styles.positionList}>
                    {POSITIONS.map(pos => (
                      <label key={pos} style={styles.positionItem}>
                        <input
                          type="checkbox"
                          checked={(editResource.positions || ['all']).includes(pos)}
                          onChange={() => toggleItem(pos, editResource.positions || ['all'], (newPos) => setEditResource({...editResource, positions: newPos}))}
                        />
                        {pos === 'all' ? 'All Employees' : pos}
                      </label>
                    ))}
                  </div>
                </div>
                <div style={styles.modalBtns}>
                  <button type="submit" style={styles.addBtn}>Save Changes</button>
                  <button type="button" onClick={() => setEditResource(null)} style={styles.cancelBtn}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Resources Table */}
        {loading ? (
          <div style={styles.loading}>Loading resources...</div>
        ) : (
          <div style={styles.tableCard}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thead}>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Categories</th>
                  <th style={styles.th}>File</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((r) => (
                  <tr key={r.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={{fontWeight: '600'}}>{r.title}</div>
                      {r.description && <div style={{fontSize: '12px', color: '#6b7280'}}>{r.description}</div>}
                    </td>
                    <td style={styles.td}>
                      <div style={{display: 'flex', gap: '4px', flexWrap: 'wrap'}}>
                        {r.resource_categories?.length > 0
                          ? r.resource_categories.map((rc, i) => {
                              const cat = categories.find(c => c.id === rc.category_id);
                              return cat ? <span key={i} style={styles.catBadge}>{cat.name}</span> : null;
                            })
                          : r.categories?.name
                          ? <span style={styles.catBadge}>{r.categories.name}</span>
                          : '—'
                        }
                      </div>
                    </td>
                    <td style={styles.td}>
                      <a href={r.file_url} target="_blank" rel="noreferrer" style={styles.fileLink}>
                        {r.file_name}
                      </a>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button onClick={() => setEditResource({
                          ...r,
                          positions: r.resource_access?.map(a => a.position) || ['all'],
                          category_ids: r.resource_categories?.map(rc => rc.category_id) || (r.categories?.id ? [r.categories.id] : [])
                        })} style={styles.editBtn}>Edit</button>
                        <button onClick={() => handleDelete(r.id)} style={styles.deleteBtn}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f8fafc', overflowX: 'hidden' },
  nav: { background: '#243C94', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' },
  navBrand: { display: 'flex', flexDirection: 'column' },
  navTitle: { color: '#ffffff', fontSize: '16px', fontWeight: '700' },
  navSubtitle: { color: '#FFA204', fontSize: '11px', fontWeight: '600', letterSpacing: '1px' },
  navRight: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  navBtn: { background: 'rgba(255,255,255,0.15)', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' },
  navBtnOutline: { background: 'transparent', color: '#ffffff', border: '2px solid rgba(255,255,255,0.5)', borderRadius: '8px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px' },
  content: { maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  headerBtns: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  pageTitle: { color: '#243C94', fontSize: '24px', fontWeight: '700', margin: 0 },
  addBtn: { background: '#FFA204', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  secondaryBtn: { background: '#243C94', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  cancelBtn: { background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontSize: '14px' },
  success: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' },
  catSection: { background: '#ffffff', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionTitle: { color: '#243C94', fontSize: '16px', fontWeight: '700', marginBottom: '16px' },
  catList: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  catTag: { background: '#eff6ff', color: '#243C94', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' },
  catBadge: { background: '#eff6ff', color: '#243C94', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  catDeleteBtn: { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px', padding: 0, lineHeight: 1 },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modalCard: { background: '#ffffff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { color: '#243C94', fontSize: '20px', fontWeight: '700', marginBottom: '24px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { color: '#374151', fontSize: '14px', fontWeight: '600' },
  input: { padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', width: '100%', boxSizing: 'border-box' },
  uploadingText: { color: '#FFA204', fontSize: '13px', fontWeight: '600' },
  uploadedText: { color: '#16a34a', fontSize: '13px', fontWeight: '600' },
  checkboxList: { display: 'flex', flexDirection: 'column', gap: '8px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb' },
  positionList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  positionItem: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' },
  modalBtns: { display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' },
  infoBox: { background: '#eff6ff', border: '1px solid #243C94', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px' },
  infoText: { color: '#243C94', fontSize: '13px', margin: 0 },
  bulkResults: { background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '4px' },
  tableCard: { background: '#ffffff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '500px' },
  thead: { background: '#f8fafc' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #e5e7eb' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#374151' },
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  editBtn: { background: '#eff6ff', color: '#243C94', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  deleteBtn: { background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },
  fileLink: { color: '#243C94', fontSize: '13px' },
  loading: { textAlign: 'center', padding: '60px', color: '#6b7280' },
};